<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { UsersService, OrganisationsService } from '@/types/generated/services.gen'

  import type {
    SuperUserUser as User,
    PatchedSuperUserUserRequest as PatchedUserRequest,
    RoleEnum
  } from '@/types/generated/types.gen'
  import * as yup from 'yup'
  import { useTranslation } from '@/composables/useTranslation'
  import { $SuperUserUser as $User } from '@/types/generated'
  import type { GenericObject } from 'vee-validate'
  import type { Option } from '@/components/admin/form/SelectField.vue'

  const { t } = useTranslation({
    en: {
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
      organization: 'Organization',
      role: 'Role',
      superuser: 'Superuser',
      superuserHelp: 'Only for JUPUS employees'
    },
    de: {
      email: 'E-Mail',
      firstName: 'Vorname',
      lastName: 'Nachname',
      organization: 'Organisation',
      role: 'Rolle',
      superuser: 'Superuser',
      superuserHelp: 'Nur für JUPUS mitarbeitende'
    }
  })

  const route = useRoute()
  const user = ref<User | null>(null)
  const organisationOptions = ref<Option[]>([])

  onMounted(async () => {
    const uuid = route.params.uuid as string
    user.value = await UsersService.usersRetrieve({ uuid })
    const organisations = await OrganisationsService.organisationsList()
    organisationOptions.value = organisations.results.map((org) => ({
      value: org.uuid,
      title: org.name
    }))
  })

  const roleOptions = computed<Option[]>(() => [
    { value: 'ADMIN', title: 'Admin' },
    { value: 'LAWYER', title: 'Lawyer' },
    { value: 'CLERK', title: 'Clerk' },
    { value: 'CLIENT', title: 'Client' },
    { value: 'PHONE_AI_USER', title: 'Phone AI user' }
  ])

  const defaultAvatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.value?.first_name || '')}+${encodeURIComponent(user.value?.last_name || '')}&background=random`

  const handleSave = async (values: GenericObject) => {
    if (user.value) {
      await UsersService.usersPartialUpdate({
        uuid: user.value.uuid,
        requestBody: values as PatchedUserRequest
      })
    }
  }
</script>

<template>
  <div
    v-if="user"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Users', to: ROUTES.ADMIN.USERS.USER_LIST },
          {
            title: `Edit ${user.uuid}`,
            to: ROUTES.ADMIN.USERS.USER_DETAIL.replace(':uuid', user.uuid || '')
          }
        ]"
      />
    </div>

    <div
      class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
        dark:bg-neutral-800"
    >
      <!-- Add avatar image here -->
      <div class="mb-5 flex justify-center">
        <img
          :src="user.avatar || defaultAvatarUrl"
          :alt="`${user.first_name} ${user.last_name}`"
          class="h-24 w-24 rounded-full object-cover"
        />
      </div>
      <div class="w-full md:w-1/2">
        <AutoSavingForm @save="handleSave">
          <InputField
            v-model="user.email"
            :label="t('email')"
            name="email"
            :rules="yup.string().email().required()"
          />
          <InputField
            v-model="user.first_name"
            :label="t('firstName')"
            name="first_name"
            :rules="yup.string().max($User.properties.first_name.maxLength).required()"
          />
          <InputField
            v-model="user.last_name"
            :label="t('lastName')"
            name="last_name"
            :rules="yup.string().max($User.properties.last_name.maxLength).required()"
          />
          <SelectField
            v-model="user.org.uuid"
            :label="t('organization')"
            :is-filterable="true"
            name="org_uuid"
            :rules="yup.string().uuid().required()"
            :options="organisationOptions"
          />
          <SelectField
            v-model="user.role as string"
            :label="t('role')"
            :is-filterable="false"
            name="role"
            :rules="
              yup
                .string()
                .nullable()
                .oneOf(['ADMIN', 'LAWYER', 'CLERK', 'CLIENT', 'PHONE_AI_USER', null])
            "
            :options="roleOptions"
          />
          <CheckboxField
            v-model="user.is_superuser"
            :label="t('superuser')"
            name="is_superuser"
            :rules="yup.boolean()"
            class="mt-2"
          />
          <p class="mt-1 text-xs text-gray-500">
            {{ t('superuserHelp') }}
          </p>
        </AutoSavingForm>
      </div>
    </div>
  </div>
</template>
